import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiCheck, FiSend, FiImage, FiCalendar, FiUser, FiMail } = FiIcons;

const StoryForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    story: '',
    date: '',
    permission: false,
    anonymous: false,
    image: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      if (files && files[0]) {
        setFormData({
          ...formData,
          [name]: files[0]
        });
        
        // Create preview URL for image
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(files[0]);
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!formData.story.trim()) newErrors.story = 'Your story is required';
    if (!formData.permission) newErrors.permission = 'Permission is required to share your story';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Create email subject and body
    const subject = encodeURIComponent(`Story Submission from ${formData.anonymous ? 'Anonymous' : formData.name}`);
    const body = encodeURIComponent(`
Name: ${formData.anonymous ? 'Anonymous' : formData.name}
Email: ${formData.email}
Date of Event: ${formData.date || 'Not specified'}
Share Anonymously: ${formData.anonymous ? 'Yes' : 'No'}

Story:
${formData.story}

Note: ${formData.image ? 'An image was attached to this story in the web form but cannot be included in this email. Please contact the sender for the image.' : 'No image was attached.'}
    `);
    
    // Open mail client with prefilled data
    window.location.href = `mailto:restorationministries@pxdmail.com?subject=${subject}&body=${body}`;
    
    // Set as submitted after a short delay
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 1000);
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      image: null
    });
    setImagePreview(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center border-b border-gray-200 p-4 bg-indigo-600">
          <h3 className="text-xl font-bold text-white">Share Your Story</h3>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-indigo-500 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-white" />
          </button>
        </div>
        
        {!submitted ? (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="bg-indigo-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700">
                If this platform has helped you find a second chance, share your experience here. Your words can inspire hope, encourage others, and strengthen our community of faith.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiUser} className="text-indigo-500" />
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Your Name
                  </label>
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.name ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiMail} className="text-indigo-500" />
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Your Email
                  </label>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.email ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiCalendar} className="text-indigo-500" />
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    When did this happen? (Optional)
                  </label>
                </div>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="story" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Story
                </label>
                <textarea
                  id="story"
                  name="story"
                  value={formData.story}
                  onChange={handleChange}
                  placeholder="Share how Restoration Ministries has impacted your life..."
                  rows="6"
                  className={`w-full px-4 py-3 border ${errors.story ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                />
                {errors.story && <p className="text-red-500 text-xs mt-1">{errors.story}</p>}
              </div>
              
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiImage} className="text-indigo-500" />
                  <label htmlFor="image" className="block text-sm font-medium text-gray-700">
                    Add a photo (Optional)
                  </label>
                </div>
                
                {!imagePreview ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors">
                    <input
                      type="file"
                      id="image"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                    <label htmlFor="image" className="cursor-pointer">
                      <div className="flex flex-col items-center">
                        <SafeIcon icon={FiImage} className="text-gray-400 text-3xl mb-2" />
                        <span className="text-sm text-gray-500">Click to upload an image</span>
                        <span className="text-xs text-gray-400 mt-1">(Max size: 5MB)</span>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="text-sm" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="md:col-span-2 space-y-3 mt-2">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="permission"
                      name="permission"
                      type="checkbox"
                      checked={formData.permission}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="permission" className={`font-medium ${errors.permission ? 'text-red-500' : 'text-gray-700'}`}>
                      I give permission to share my story to inspire others
                    </label>
                    {errors.permission && <p className="text-red-500 text-xs mt-1">{errors.permission}</p>}
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="anonymous"
                      name="anonymous"
                      type="checkbox"
                      checked={formData.anonymous}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="anonymous" className="font-medium text-gray-700">
                      Share my story anonymously
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} />
                    <span>Share Story</span>
                  </>
                )}
              </motion.button>
              <p className="text-xs text-gray-500 text-center mt-3">
                By submitting, you acknowledge that your story may be shared on our website or in other materials.
              </p>
            </div>
          </form>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCheck} className="text-green-600 text-2xl" />
            </div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">Thank You For Sharing Your Story</h4>
            <p className="text-gray-600 mb-6">
              Your story has been submitted successfully to restorationministries@pxdmail.com. Stories like yours help inspire others on their journey to restoration.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default StoryForm;